import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../api/client.js';
import { useAuth } from '../../auth/AuthContext.jsx';
import PasswordInput from '../../components/PasswordInput.jsx';

export default function AdminUsers() {
  const { user, startImpersonation } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null); // user object or 'new'
  const [resetTarget, setResetTarget] = useState(null);

  const list = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get('/admin/users').then(r => r.data.data),
  });
  const branches = useQuery({
    queryKey: ['branches'],
    queryFn: () => api.get('/branches').then(r => r.data.data),
  });

  const impersonate = useMutation({
    mutationFn: (userId) => api.post(`/admin/users/${userId}/impersonate`).then(r => r.data.data),
    onSuccess: ({ token, user: targetUser }) => {
      startImpersonation({ token, user: targetUser });
      nav('/');
    },
  });

  const filtered = (list.data || [])
    .filter(u => showInactive || u.status === 'active')
    .filter(u => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (u.full_name || '').toLowerCase().includes(s)
          || (u.email || '').toLowerCase().includes(s)
          || (u.branch_name || '').toLowerCase().includes(s);
    });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <div className="section-eyebrow">Admin</div>
          <h1 className="text-2xl">Users</h1>
          <div className="rule-crimson" />
        </div>
        <button className="btn-primary" onClick={() => setEditing('new')}>+ New user</button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input flex-1" placeholder="Search name, email, branch…"
               value={q} onChange={e => setQ(e.target.value)} />
        <label className="flex items-center gap-1 text-sm text-muted">
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      {list.isLoading && <div className="text-muted">Loading…</div>}

      <div className="space-y-1">
        {filtered.map(u => {
          const canImpersonate = user?.role === 'superadmin' && u.role !== 'superadmin' && u.id !== user.id && u.status === 'active';
          return (
            <div key={u.id} className={`card p-3 ${u.status !== 'active' ? 'opacity-60' : ''}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium text-navy truncate">{u.full_name}</span>
                    <span className="text-xs uppercase tracking-wider text-muted">{u.role.replace('_', ' ')}</span>
                    {u.must_change_password && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 rounded">must change pw</span>}
                    {u.status !== 'active' && <span className="text-[10px] bg-panel text-muted px-1.5 rounded">{u.status}</span>}
                  </div>
                  <div className="text-xs text-muted truncate">
                    {u.email} · {u.branch_name || '—'}
                    {u.last_login_at && ` · last login ${new Date(u.last_login_at).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {canImpersonate && (
                    <button className="btn-ghost text-xs"
                            disabled={impersonate.isPending}
                            onClick={() => impersonate.mutate(u.id)}>
                      👁 View as
                    </button>
                  )}
                  <button className="btn-ghost text-xs" onClick={() => setEditing(u)}>Edit</button>
                  <button className="btn-ghost text-xs" onClick={() => setResetTarget(u)}>Reset pw</button>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && !list.isLoading && (
          <div className="card p-3 text-muted text-sm">No users match.</div>
        )}
      </div>

      {editing === 'new' && (
        <NewUserModal
          onClose={() => setEditing(null)}
          branches={branches.data || []}
          currentRole={user.role}
          onCreated={() => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setEditing(null); }}
        />
      )}
      {editing && editing !== 'new' && (
        <EditUserModal
          target={editing}
          branches={branches.data || []}
          currentRole={user.role}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setEditing(null); }}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          target={resetTarget}
          onClose={() => setResetTarget(null)}
          onDone={() => { qc.invalidateQueries({ queryKey: ['admin-users'] }); setResetTarget(null); }}
        />
      )}
    </div>
  );
}

function NewUserModal({ onClose, onCreated, branches, currentRole }) {
  const allowedRoles = currentRole === 'superadmin' ? ['dealer_admin', 'branch_admin', 'sales']
    : currentRole === 'dealer_admin' ? ['branch_admin', 'sales']
    : ['sales'];
  const [form, setForm] = useState({
    email: '', full_name: '', password: '',
    role: allowedRoles[allowedRoles.length - 1], branch_id: branches[0]?.id || '',
    phone: '',
  });
  const m = useMutation({
    mutationFn: (data) => api.post('/users', data).then(r => r.data.data),
    onSuccess: onCreated,
  });
  return (
    <Modal title="New user" onClose={onClose}>
      <Field label="Full name"><input className="input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></Field>
      <Field label="Email"><input className="input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
      <Field label="Phone"><input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      <Field label="Password (12+ chars)"><PasswordInput autoComplete="new-password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></Field>
      <Field label="Role">
        <select className="input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
          {allowedRoles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      <Field label="Branch">
        <select className="input" value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value ? parseInt(e.target.value, 10) : '' })}>
          <option value="">—</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </Field>
      {m.isError && <div className="text-crimson text-sm">{m.error?.response?.data?.error || 'Failed'}</div>}
      <ModalActions onClose={onClose} disabled={m.isPending || !form.email || !form.password || !form.full_name}
                    onSave={() => m.mutate(form)} pending={m.isPending} />
    </Modal>
  );
}

// Role-based permission defaults — mirrors server/src/middleware/permissions.js
const PERM_DEFS = [
  { key: 'can_export',               label: 'Export CSV data',          defaults: { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false } },
  { key: 'can_edit_customers',       label: 'Edit customer notes',      defaults: { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false } },
  { key: 'can_delete_opportunities', label: 'Delete pipeline deals',    defaults: { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false } },
];

function roleDefault(role, perm) {
  return perm.defaults[role] ?? false;
}

function EditUserModal({ target, onClose, onSaved, branches, currentRole }) {
  const canEditPermissions = currentRole === 'superadmin' || currentRole === 'dealer_admin';
  const isSuperadmin = currentRole === 'superadmin';
  const allowedRoles = currentRole === 'superadmin' ? ['superadmin','dealer_admin','branch_admin','sales']
    : currentRole === 'dealer_admin' ? ['dealer_admin','branch_admin','sales']
    : ['sales'];

  const [form, setForm] = useState({
    full_name: target.full_name || '',
    role: target.role,
    branch_id: target.branch_id || '',
    status: target.status,
    phone: target.phone || '',
    daily_digest_opt_in: target.daily_digest_opt_in || false,
    sales_function: target.sales_function || '',
    followup_digest_always_include: target.followup_digest_always_include || false,
  });

  // Permissions: explicit overrides from target.permissions, null = use role default
  const [perms, setPerms] = useState(() => {
    const base = {};
    for (const p of PERM_DEFS) {
      const explicit = target.permissions?.[p.key];
      base[p.key] = typeof explicit === 'boolean' ? explicit : null;
    }
    return base;
  });
  const [canSeeSales, setCanSeeSales] = useState(target.can_see_sales || false);

  // Effective role for default display (may change when form.role changes)
  const effectiveRole = form.role || target.role;

  const m = useMutation({
    mutationFn: (data) => {
      const payload = { ...data };
      payload.branch_id = data.branch_id ? parseInt(data.branch_id, 10) : null;
      if (isSuperadmin) {
        payload.sales_function = data.sales_function || null;
        payload.followup_digest_always_include = data.followup_digest_always_include;
      } else {
        delete payload.sales_function;
        delete payload.followup_digest_always_include;
      }
      if (canEditPermissions) {
        // Only send explicit overrides (non-null). Strip keys where value matches role default.
        const explicitPerms = {};
        for (const p of PERM_DEFS) {
          if (perms[p.key] !== null) {
            explicitPerms[p.key] = perms[p.key];
          }
        }
        payload.permissions = explicitPerms;
        payload.can_see_sales = canSeeSales;
      }
      return api.put(`/admin/users/${target.id}`, payload).then(r => r.data.data);
    },
    onSuccess: onSaved,
  });

  function togglePerm(key) {
    const cur = perms[key];
    const def = roleDefault(effectiveRole, PERM_DEFS.find(p => p.key === key));
    if (cur === null) {
      // null (inheriting default) → flip to explicit opposite of default
      setPerms(p => ({ ...p, [key]: !def }));
    } else if (cur !== def) {
      // explicit override that differs from default → reset to default (null)
      setPerms(p => ({ ...p, [key]: null }));
    } else {
      // explicit override matching default → flip to explicit opposite
      setPerms(p => ({ ...p, [key]: !cur }));
    }
  }

  return (
    <Modal title={`Edit ${target.full_name}`} onClose={onClose}>
      <div className="text-xs text-muted mb-2">{target.email}</div>
      <Field label="Full name"><input className="input" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} /></Field>
      <Field label="Phone"><input className="input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
      <Field label="Role">
        <select className="input" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
          {allowedRoles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      <Field label="Branch">
        <select className="input" value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })}>
          <option value="">—</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select className="input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
      </Field>
      <label className="flex items-center gap-1.5 text-sm">
        <input type="checkbox" checked={form.daily_digest_opt_in}
               onChange={e => setForm({ ...form, daily_digest_opt_in: e.target.checked })} />
        Receives daily standup digest (email + push)
      </label>

      {isSuperadmin && (
        <>
          <Field label="Sales function (drives the 6am follow-up digest + sales reports)">
            <select className="input" value={form.sales_function}
                    onChange={e => setForm({ ...form, sales_function: e.target.value })}>
              <option value="">— none —</option>
              <option value="bdm">BDM</option>
              <option value="bdc">BDC</option>
              <option value="aftermarket">Aftermarket</option>
              <option value="equipment">Equipment</option>
            </select>
          </Field>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={form.followup_digest_always_include}
                   onChange={e => setForm({ ...form, followup_digest_always_include: e.target.checked })} />
            Always include in follow-up digest (even if inactive or no sales function)
          </label>
        </>
      )}

      {canEditPermissions && (
        <div>
          <div className="label mb-1">Permissions</div>
          <div className="text-[11px] text-muted mb-2">
            Defaults shown in grey. Explicit overrides shown in color — click again to reset to role default.
          </div>
          <div className="space-y-1.5 rounded border border-gray-200 p-2 bg-gray-50">
            {/* Sales data access — stored as separate boolean column */}
            <PermRow
              label="View sales & pipeline data"
              isDefault={false}
              effective={canSeeSales}
              isOverride={canSeeSales !== (effectiveRole === 'superadmin')}
              onToggle={() => setCanSeeSales(v => !v)}
            />
            {PERM_DEFS.map(p => {
              const def = roleDefault(effectiveRole, p);
              const explicit = perms[p.key];
              const effective = explicit !== null ? explicit : def;
              const isOverride = explicit !== null;
              return (
                <PermRow
                  key={p.key}
                  label={p.label}
                  isDefault={!isOverride}
                  effective={effective}
                  isOverride={isOverride}
                  onToggle={() => togglePerm(p.key)}
                />
              );
            })}
          </div>
          <button
            type="button"
            className="text-[11px] text-muted hover:text-navy mt-1 underline"
            onClick={() => {
              const reset = {};
              for (const p of PERM_DEFS) reset[p.key] = null;
              setPerms(reset);
            }}
          >
            Reset all to role defaults
          </button>
        </div>
      )}

      {m.isError && <div className="text-crimson text-sm">{m.error?.response?.data?.error || 'Failed'}</div>}
      <ModalActions onClose={onClose} disabled={m.isPending} onSave={() => m.mutate(form)} pending={m.isPending} />
    </Modal>
  );
}

function PermRow({ label, effective, isOverride, isDefault, onToggle }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className={`text-xs ${isOverride ? 'text-navy font-medium' : 'text-gray-500'}`}>{label}</span>
      <div className="flex items-center gap-1.5 shrink-0">
        {isOverride && (
          <span className="text-[10px] text-amber-600 font-medium uppercase tracking-wide">override</span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${
            effective ? 'bg-green-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
              effective ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function ResetPasswordModal({ target, onClose, onDone }) {
  const [tempPassword, setTempPassword] = useState('');
  const [generated, setGenerated] = useState(null);

  const m = useMutation({
    mutationFn: () => {
      const body = tempPassword.trim() ? { password: tempPassword.trim() } : {};
      return api.post(`/admin/users/${target.id}/reset-password`, body).then(r => r.data.data);
    },
    onSuccess: (d) => { setGenerated(d.temporary_password); },
  });

  return (
    <Modal title={`Reset password — ${target.full_name}`} onClose={onClose}>
      {!generated && (
        <>
          <p className="text-sm text-muted mb-2">
            User will be forced to change their password on next login.
            Leave blank to auto-generate.
          </p>
          <Field label="Temporary password (optional)">
            <PasswordInput autoComplete="new-password"
                           value={tempPassword}
                           onChange={e => setTempPassword(e.target.value)}
                           placeholder="Auto-generate if blank" />
          </Field>
          {m.isError && <div className="text-crimson text-sm">{m.error?.response?.data?.error || 'Failed'}</div>}
          <ModalActions onClose={onClose} disabled={m.isPending} onSave={() => m.mutate()}
                        saveLabel="Reset" pending={m.isPending} />
        </>
      )}
      {generated && (
        <>
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm">
            <div className="font-semibold mb-1">New password set:</div>
            <div className="font-mono text-base bg-white px-2 py-1 rounded border select-all">{generated}</div>
            <div className="text-xs text-muted mt-2">
              Copy this now — you won't see it again. Hand it off securely (text, signal, in person).
              {target.email} will be forced to change it on next login.
            </div>
          </div>
          <ModalActions onClose={onDone} onSave={onDone} saveLabel="Done" />
        </>
      )}
    </Modal>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-navy/50 flex items-center justify-center p-4 z-40" onClick={onClose}>
      <div className="bg-white rounded p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) { return <div><label className="label">{label}</label>{children}</div>; }
function ModalActions({ onClose, onSave, disabled, saveLabel = 'Save', pending }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button className="btn-ghost" onClick={onClose}>Cancel</button>
      <button className="btn-primary" disabled={disabled} onClick={onSave}>
        {pending ? 'Working…' : saveLabel}
      </button>
    </div>
  );
}
